import { Copy, Receipt, Trash2, Wrench } from "lucide-react";
import type { DailyEntryOptions } from "@crewledger/shared/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { EquipmentSelector } from "./components/equipment-selector";
import { RowWarningsList } from "./components/row-warnings-list";
import {
  buildRowWarningsForRow,
  previewRowPay,
  type DailyEntryUiRow,
} from "./utils";

const FLAG_KEYS = [
  { key: "isWeekend" as const, label: "Wknd" },
  { key: "isHoliday" as const, label: "Hol" },
  { key: "isNightShift" as const, label: "Night" },
  { key: "isHazard" as const, label: "Haz" },
  { key: "isTraining" as const, label: "Train" },
  { key: "isManualOvertime" as const, label: "OT" },
  { key: "isManualDoubleTime" as const, label: "DT" },
];

interface DailyEntryMobileBoardProps {
  rows: DailyEntryUiRow[];
  options: DailyEntryOptions;
  date: string;
  canEdit: boolean;
  canViewPayroll: boolean;
  canViewEquipmentCosts: boolean;
  onChangeRow: (clientId: string, patch: Partial<DailyEntryUiRow>) => void;
  onRemoveRow: (clientId: string) => void;
  onCopyYesterday: (clientId: string) => void;
  onOpenBreakdown: (clientId: string) => void;
  yesterdayRows: DailyEntryUiRow[];
}

export function DailyEntryMobileBoard({
  rows,
  options,
  date,
  canEdit,
  canViewPayroll,
  canViewEquipmentCosts,
  onChangeRow,
  onRemoveRow,
  onCopyYesterday,
  onOpenBreakdown,
  yesterdayRows,
}: DailyEntryMobileBoardProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed bg-card-white px-4 py-10 text-center text-sm text-muted-foreground lg:hidden">
        No workers on the board. Add a crew or worker to start logging hours.
      </div>
    );
  }

  return (
    <div className="block space-y-3 lg:hidden">
      {rows.map((row) => {
        const preview = previewRowPay(row, options, date, canViewPayroll);
        const warnings = buildRowWarningsForRow(row, options);
        const hasErrors = warnings.some((w) => w.severity === "error");
        const yesterday = yesterdayRows.find((y) => y.workerId === row.workerId);
        const equipment = row.equipment.length
          ? row.equipment
          : [{ equipmentId: "", usageHours: 0 }];
        const needsOverride = row.equipment.some((e) => e.usageHours > row.hours);

        return (
          <article
            key={row.clientId}
            className={cn(
              "rounded-xl border-2 bg-card-white p-4 shadow-[var(--shadow-card)]",
              hasErrors ? "border-red-200 bg-red-50/30" : warnings.length ? "border-amber-200" : "border-border",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-mono text-xs font-semibold text-navy-blueprint">{row.employeeId}</p>
                <h3 className="text-base font-semibold text-navy-deep">{row.workerName}</h3>
                {row.crew ? <p className="text-xs text-muted-foreground">{row.crew}</p> : null}
              </div>
              <div className="flex gap-1">
                {canEdit && yesterday ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0"
                    title="Same as yesterday"
                    onClick={() => { onCopyYesterday(row.clientId); }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 text-red-danger"
                    onClick={() => { onRemoveRow(row.clientId); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Hours
                </label>
                <Input
                  type="number"
                  min={0}
                  max={24}
                  step={0.5}
                  disabled={!canEdit}
                  value={row.hours}
                  onChange={(e) =>
                    { onChangeRow(row.clientId, { hours: Number(e.target.value) || 0 }); }
                  }
                  className="mt-1 h-12 text-xl font-bold tabular-nums"
                  inputMode="decimal"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Job / cost code
                </label>
                <div className="mt-1 space-y-1">
                  <Input
                    list={`mobile-job-${row.clientId}`}
                    disabled={!canEdit}
                    value={row.jobCode ?? ""}
                    onChange={(e) => { onChangeRow(row.clientId, { jobCode: e.target.value }); }}
                    className="h-10 font-mono text-sm"
                    placeholder="JOB-100"
                  />
                  <Input
                    disabled={!canEdit}
                    value={row.costCode ?? ""}
                    onChange={(e) => { onChangeRow(row.clientId, { costCode: e.target.value }); }}
                    className="h-9 font-mono text-xs"
                    placeholder="CC-01"
                  />
                  <datalist id={`mobile-job-${row.clientId}`}>
                    {options.recentJobCodes.map((code) => (
                      <option key={code} value={code} />
                    ))}
                  </datalist>
                </div>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Equipment
                </p>
                <p className="text-[10px] text-muted-foreground">
                  Eq hrs {formatHours(row.equipment.reduce((acc, e) => acc + e.usageHours, 0))}
                </p>
              </div>
              {equipment.map((item, index) => (
                <EquipmentSelector
                  key={`${row.clientId}-eq-${index}`}
                  equipment={options.equipment}
                  value={item.equipmentId}
                  usageHours={item.usageHours}
                  laborHours={row.hours}
                  canEdit={canEdit}
                  canViewEquipmentCosts={canViewEquipmentCosts}
                  compact
                  onEquipmentChange={(equipmentId) => {
                    const next = [...row.equipment];
                    if (!next[index]) {
                      next[index] = { equipmentId, usageHours: 0 };
                    } else {
                      next[index] = { ...next[index], equipmentId };
                    }
                    onChangeRow(row.clientId, { equipment: next });
                  }}
                  onUsageHoursChange={(usageHours) => {
                    const next = [...row.equipment];
                    next[index] = { ...next[index]!, usageHours };
                    onChangeRow(row.clientId, { equipment: next });
                  }}
                />
              ))}
              {canEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() =>
                    { onChangeRow(row.clientId, {
                      equipment: [...row.equipment, { equipmentId: "", usageHours: 0 }],
                    }); }
                  }
                >
                  <Wrench className="mr-1 h-3 w-3" />
                  Add equipment
                </Button>
              ) : null}
              {needsOverride ? (
                <Input
                  disabled={!canEdit}
                  placeholder="Equipment hours override reason"
                  value={
                    row.equipment.find((e) => e.usageHours > row.hours)
                      ?.equipmentHoursOverrideReason ?? ""
                  }
                  onChange={(e) => {
                    const next = row.equipment.map((item) =>
                      item.usageHours > row.hours
                        ? { ...item, equipmentHoursOverrideReason: e.target.value }
                        : item,
                    );
                    onChangeRow(row.clientId, { equipment: next });
                  }}
                  className="h-9 text-xs"
                />
              ) : null}
            </div>

            <div className="mt-3">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Notes
              </label>
              <Input
                disabled={!canEdit}
                value={row.notes ?? ""}
                onChange={(e) => { onChangeRow(row.clientId, { notes: e.target.value }); }}
                className="mt-1 h-10"
                placeholder="Notes"
              />
            </div>

            <div className="mt-3 flex flex-wrap gap-1">
              {FLAG_KEYS.map((flag) => (
                <button
                  key={flag.key}
                  type="button"
                  disabled={!canEdit}
                  onClick={() =>
                    { onChangeRow(row.clientId, {
                      flags: { ...row.flags, [flag.key]: !row.flags[flag.key] },
                    }); }
                  }
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs font-semibold",
                    row.flags[flag.key]
                      ? "border-navy-deep bg-navy-deep text-white"
                      : "border-border bg-white text-muted-foreground",
                  )}
                >
                  {flag.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              aria-label="View pay breakdown"
              onClick={() => { onOpenBreakdown(row.clientId); }}
              className="mt-3 flex w-full items-center justify-between rounded-lg border bg-bg-light px-3 py-2 text-left"
            >
              <span className="flex items-center gap-1.5 text-sm font-semibold text-navy-deep">
                <Receipt className="h-4 w-4" />
                {canViewPayroll ? formatCurrency(preview.grossPay ?? 0) : "Pay preview"}
              </span>
              <span className="text-xs text-muted-foreground">
                R {formatHours(preview.regularHours)} · OT {formatHours(preview.overtimeHours)}
              </span>
            </button>

            {warnings.length ? <RowWarningsList warnings={warnings} className="mt-2" /> : null}
          </article>
        );
      })}
    </div>
  );
}
